import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, Paper, CircularProgress, Chip,
  FormControl, InputLabel, Select, MenuItem, TextField, Button, Switch,
  IconButton, Tooltip, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControlLabel,
} from '@mui/material';
import { Delete as DeleteIcon, ContentCopy as CopyIcon, Share as ShareIcon, Edit as EditIcon } from '@mui/icons-material';
import { apiRequest } from '../utils/api';

const EMPTY_RULE = {
  source_account_id: '', target_account_id: '', tag_match: '',
  qty_mode: 'mirror', qty_value: 1, qty_multiplier: 1, max_contracts: 10,
  mode: 'dry_run', enabled: true,
};

const EVENT_COLORS = { dry_run: 'info', submitted: 'success', failed: 'error', skipped: 'warning' };

const CopyTrading = () => {
  const [accounts, setAccounts] = useState([]);
  const [rules, setRules] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_RULE);
  const [saving, setSaving] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [customTag, setCustomTag] = useState(false);
  const [sharedWithMe, setSharedWithMe] = useState([]);
  const [shareDialog, setShareDialog] = useState(null); // {rule, users:[], selected:Set}
  const [enableTargets, setEnableTargets] = useState({}); // ruleId -> target_account_id

  // Load observed order tags when a source account is picked — they come from
  // trades.tags populated by sync.
  useEffect(() => {
    if (!form.source_account_id) { setAvailableTags([]); return; }
    setCustomTag(false);
    apiRequest(`/api/copy-trading/tags?accountId=${form.source_account_id}`)
      .then(r => setAvailableTags(r.data?.tags || []))
      .catch(() => setAvailableTags([]));
  }, [form.source_account_id]);

  const accountName = (id) => accounts.find(a => String(a.id) === String(id))?.name || `#${id}`;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [aR, rR, eR, sR] = await Promise.all([
        apiRequest('/api/accounts'),
        apiRequest('/api/copy-trading/rules').catch(e => ({ error: e })),
        apiRequest('/api/copy-trading/events?limit=100').catch(() => ({ data: { events: [] } })),
        apiRequest('/api/copy-trading/shared').catch(() => ({ data: { shared: [] } })),
      ]);
      const all = aR.data?.accounts || (Array.isArray(aR.data) ? aR.data : []);
      setAccounts(all);
      if (rR.error) {
        const msg = rR.error.response?.data?.error || rR.error.message || 'Failed to load rules';
        setError(msg.includes('copy_trade_rules') ? 'Copy trading tables not yet created — run migration 039 on the server.' : msg);
        setRules([]);
      } else {
        setRules(rR.data?.rules || []);
      }
      setEvents(eR.data?.events || []);
      setSharedWithMe(sR.data?.shared || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveRule = async () => {
    setSaving(true);
    try {
      const editing = form.id;
      await apiRequest(editing ? `/api/copy-trading/rules/${form.id}` : '/api/copy-trading/rules', {
        method: editing ? 'PUT' : 'POST',
        body: JSON.stringify(form),
      });
      setDialogOpen(false);
      setForm(EMPTY_RULE);
      fetchAll();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (rule) => {
    setForm({ ...EMPTY_RULE, ...rule });
    setCustomTag(true); // existing tag won't be in the observed-tags list
    setDialogOpen(true);
  };

  const toggleRule = async (rule, field, value) => {
    try {
      await apiRequest(`/api/copy-trading/rules/${rule.id}`, { method: 'PUT', body: JSON.stringify({ [field]: value }) });
      fetchAll();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const deleteRule = async (id) => {
    try {
      await apiRequest(`/api/copy-trading/rules/${id}`, { method: 'DELETE' });
      fetchAll();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  // Share dialog: load users + current recipients for this rule.
  const openShareDialog = async (rule) => {
    try {
      const [uR, sR] = await Promise.all([
        apiRequest('/api/copy-trading/shareable-users'),
        apiRequest(`/api/copy-trading/rules/${rule.id}/shares`),
      ]);
      setShareDialog({
        rule,
        users: uR.data?.users || [],
        selected: new Set(sR.data?.userIds || []),
      });
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const saveShares = async () => {
    if (!shareDialog) return;
    setSaving(true);
    try {
      await apiRequest(`/api/copy-trading/rules/${shareDialog.rule.id}/shares`, {
        method: 'PUT',
        body: JSON.stringify({ userIds: [...shareDialog.selected] }),
      });
      setShareDialog(null);
      fetchAll();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const enableShared = async (ruleId) => {
    const target_account_id = enableTargets[ruleId];
    if (!target_account_id) { setError('Pick a target account first'); return; }
    try {
      await apiRequest(`/api/copy-trading/shared/${ruleId}/enable`, {
        method: 'POST',
        body: JSON.stringify({ target_account_id }),
      });
      fetchAll();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  // Source must be Schwab — order tags only exist on Schwab orders and the
  // poller only watches Schwab accounts. Target = the tradable account types
  // the copy service can actually submit to (robinhood-mcp excluded for now).
  const apiType = (a) => String(a.api_type || a.apiType || '').toLowerCase();
  const schwabAccounts = accounts.filter(a => apiType(a) === 'schwab');
  const targetAccounts = accounts.filter(a => ['schwab', 'robinhood', 'robinhood-api'].includes(apiType(a)));

  // shared_by_user_id -> email, for badges on cloned rules
  const sharedByEmail = new Map(sharedWithMe.map(s => [s.rule.user_id, s.shared_by_email]));

  const acctSelect = (label, field, list) => (
    <FormControl size="small" fullWidth required>
      <InputLabel>{label}</InputLabel>
      <Select value={form[field]} label={label} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}>
        {list.map(a => (
          <MenuItem key={a.id} value={a.id}>
            {a.name || `#${a.id}`}{apiType(a) !== 'schwab' ? ` (${apiType(a)})` : ''}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Copy Trading</Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Replicate filled orders from a source Schwab account to a target account when the
        source order carries a matching tag. Rules start in dry-run mode (records + notifies
        what it would copy without submitting). Live mode additionally requires
        COPY_TRADING_ENABLED on the server.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1">Rules</Typography>
                <Button variant="contained" size="small" startIcon={<CopyIcon />}
                  onClick={() => { setForm(EMPTY_RULE); setCustomTag(false); setDialogOpen(true); }}
                  disabled={schwabAccounts.length === 0 || accounts.length < 2}>
                  New Rule
                </Button>
              </Box>
              {schwabAccounts.length === 0 && (
                <Alert severity="info">Copy trading needs at least one active Schwab account as the source.</Alert>
              )}
              {rules.length === 0 ? (
                <Typography color="textSecondary" variant="body2">No rules configured.</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Enabled</TableCell>
                        <TableCell>Source → Target</TableCell>
                        <TableCell>Tag</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Max Contracts</TableCell>
                        <TableCell>Mode</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rules.map(r => (
                        <TableRow key={r.id} hover>
                          <TableCell>
                            <Switch size="small" checked={!!r.enabled}
                              onChange={e => toggleRule(r, 'enabled', e.target.checked)} />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>
                            {accountName(r.source_account_id)} → {accountName(r.target_account_id)}
                            {r.shared_from_rule_id && (
                              <Chip label={`shared by ${sharedByEmail.get(r.shared_by_user_id) || 'another user'}`}
                                size="small" color="primary" variant="outlined" sx={{ ml: 1, fontSize: '0.7rem' }} />
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{r.tag_match}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>
                            {r.qty_mode === 'fixed' ? `Fixed ${r.qty_value}` : `Mirror${parseFloat(r.qty_multiplier) !== 1 ? ` ×${parseFloat(r.qty_multiplier)}` : ''}`}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{r.max_contracts}</TableCell>
                          <TableCell>
                            <Tooltip title={r.mode === 'live' ? 'Submitting real orders' : 'Recording + notifying only — click to go live'}
                              placement="top" arrow>
                              <Chip
                                label={r.mode === 'live' ? 'Live' : 'Dry run'}
                                size="small"
                                color={r.mode === 'live' ? 'error' : 'default'}
                                variant={r.mode === 'live' ? 'filled' : 'outlined'}
                                onClick={() => toggleRule(r, 'mode', r.mode === 'live' ? 'dry_run' : 'live')}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ width: 80, whiteSpace: 'nowrap' }}>
                            {!r.shared_from_rule_id && (
                              <Tooltip title="Share this rule with other users" arrow>
                                <IconButton size="small" onClick={() => openShareDialog(r)}>
                                  <ShareIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <IconButton size="small" onClick={() => openEdit(r)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => deleteRule(r.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {sharedWithMe.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Shared With Me</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Copy rules other users shared with you. Enable one with your target account to start copying its fills.
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Shared by</TableCell>
                        <TableCell>Tag</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Max Contracts</TableCell>
                        <TableCell>Target Account</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sharedWithMe.map(({ rule: r, shared_by_email, clone }) => (
                        <TableRow key={r.id} hover>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{shared_by_email}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{r.tag_match}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>
                            {r.qty_mode === 'fixed' ? `Fixed ${r.qty_value}` : `Mirror${parseFloat(r.qty_multiplier) !== 1 ? ` ×${parseFloat(r.qty_multiplier)}` : ''}`}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{r.max_contracts}</TableCell>
                          <TableCell sx={{ minWidth: 160 }}>
                            {clone ? (
                              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{accountName(clone.target_account_id)}</Typography>
                            ) : (
                              <FormControl size="small" fullWidth>
                                <Select
                                  value={enableTargets[r.id] || ''}
                                  displayEmpty
                                  onChange={e => setEnableTargets(t => ({ ...t, [r.id]: e.target.value }))}
                                >
                                  <MenuItem value="" disabled><em>Pick account…</em></MenuItem>
                                  {targetAccounts.map(a => (
                                    <MenuItem key={a.id} value={a.id}>{a.name || `#${a.id}`}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                          </TableCell>
                          <TableCell>
                            {clone ? (
                              <Chip
                                label={clone.enabled ? 'Enabled (dry run)' : 'Disabled'}
                                size="small" color={clone.enabled ? 'success' : 'default'} variant="outlined"
                                onClick={() => toggleRule(clone, 'enabled', !clone.enabled)}
                              />
                            ) : (
                              <Button size="small" variant="contained"
                                disabled={!enableTargets[r.id]}
                                onClick={() => enableShared(r.id)}>
                                Enable
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Recent Events</Typography>
              {events.length === 0 ? (
                <Typography color="textSecondary" variant="body2">No copy events yet.</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Time</TableCell>
                        <TableCell>Source Order</TableCell>
                        <TableCell>Accounts</TableCell>
                        <TableCell>Legs</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Target Order</TableCell>
                        <TableCell>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {events.map(ev => (
                        <TableRow key={ev.id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                            {new Date(ev.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{ev.source_order_id}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>
                            {accountName(ev.source_account_id)} → {accountName(ev.target_account_id)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', maxWidth: 300 }}>
                            {(ev.order_snapshot?.legs || []).map(l => `${l.instruction} ${l.quantity}x ${l.symbol}`).join('; ') || '-'}
                          </TableCell>
                          <TableCell>
                            <Chip label={ev.status} size="small" color={EVENT_COLORS[ev.status] || 'default'} variant="outlined" />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{ev.target_order_id || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', color: 'error.main', maxWidth: 200 }}>{ev.error || ''}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? 'Edit Copy Rule' : 'New Copy Rule'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {acctSelect('Source Account (Schwab only — tags come from Schwab orders)', 'source_account_id', schwabAccounts)}
            {acctSelect('Target Account', 'target_account_id', targetAccounts.filter(a => String(a.id) !== String(form.source_account_id)))}
            {customTag ? (
              <TextField size="small" label="Tag (exact match)" required fullWidth
                helperText="Type a custom tag"
                value={form.tag_match}
                onChange={e => setForm(f => ({ ...f, tag_match: e.target.value }))} />
            ) : (
              <FormControl size="small" fullWidth required>
                <InputLabel>Tag</InputLabel>
                <Select
                  value={availableTags.includes(form.tag_match) ? form.tag_match : ''}
                  label="Tag"
                  disabled={!form.source_account_id}
                  onChange={e => {
                    if (e.target.value === '__custom__') {
                      setCustomTag(true);
                      setForm(f => ({ ...f, tag_match: '' }));
                    } else {
                      setForm(f => ({ ...f, tag_match: e.target.value }));
                    }
                  }}>
                  {availableTags.map(t => (
                    <MenuItem key={t} value={t} sx={{ fontFamily: 'monospace' }}>{t}</MenuItem>
                  ))}
                  <MenuItem value="__custom__">Custom…</MenuItem>
                </Select>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                  {form.source_account_id
                    ? (availableTags.length ? 'Tags seen on this account’s imported orders' : 'No order tags imported for this account yet')
                    : 'Pick a source account first'}
                </Typography>
              </FormControl>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Quantity</InputLabel>
                <Select value={form.qty_mode} label="Quantity"
                  onChange={e => setForm(f => ({ ...f, qty_mode: e.target.value }))}>
                  <MenuItem value="mirror">Mirror source</MenuItem>
                  <MenuItem value="fixed">Fixed per leg</MenuItem>
                </Select>
              </FormControl>
              {form.qty_mode === 'fixed' && (
                <TextField size="small" type="number" label="Contracts per leg" sx={{ width: 140 }}
                  inputProps={{ min: 1 }}
                  value={form.qty_value}
                  onChange={e => setForm(f => ({ ...f, qty_value: e.target.value }))} />
              )}
              {form.qty_mode === 'mirror' && (
                <TextField size="small" type="number" label="Mirror × (e.g. 2 = double)" sx={{ width: 170 }}
                  inputProps={{ min: 0.1, max: 20, step: 0.5 }}
                  value={form.qty_multiplier ?? 1}
                  onChange={e => setForm(f => ({ ...f, qty_multiplier: e.target.value }))} />
              )}
              <TextField size="small" type="number" label="Max contracts" sx={{ width: 130 }}
                inputProps={{ min: 1, max: 100 }}
                value={form.max_contracts}
                onChange={e => setForm(f => ({ ...f, max_contracts: e.target.value }))} />
            </Box>
            <FormControlLabel
              control={<Switch checked={form.mode === 'live'}
                onChange={e => setForm(f => ({ ...f, mode: e.target.checked ? 'live' : 'dry_run' }))} />}
              label={form.mode === 'live' ? 'Live — submits real orders (requires server flag)' : 'Dry run — record + notify only'} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveRule}
            disabled={saving || !form.source_account_id || !form.target_account_id || !form.tag_match}>
            {form.id ? 'Save Changes' : 'Create Rule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share rule dialog */}
      <Dialog open={!!shareDialog} onClose={() => setShareDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Share Rule</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Tag <b>{shareDialog?.rule.tag_match}</b> — selected users can enable this definition with their own target account.
          </Typography>
          {(shareDialog?.users || []).length === 0 ? (
            <Typography variant="body2">No other users found.</Typography>
          ) : shareDialog.users.map(u => (
            <FormControlLabel
              key={u.id}
              control={
                <Switch
                  size="small"
                  checked={shareDialog.selected.has(u.id)}
                  onChange={e => {
                    const next = new Set(shareDialog.selected);
                    e.target.checked ? next.add(u.id) : next.delete(u.id);
                    setShareDialog(d => ({ ...d, selected: next }));
                  }}
                />
              }
              label={u.email}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveShares} disabled={saving}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CopyTrading;
